import React, { useEffect, useState } from 'react';

import { Checkbox as AntdCheckbox, Typography, Row } from 'antd';
import { CheckboxOptionType } from 'antd/lib/checkbox';

import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';

import { useClient, useUser } from '../../../contexts';
import { Associative } from '../../../types';

dayjs.extend(customParseFormat);

interface CheckboxProps {
  id: string;
  name: string;
  placeholder: string;
  options: CheckboxOptionType[];
  onChange: (checkedValues: any) => void;
  defaultChecked: string[];
  isAssociation?: boolean;
  isDisabled?: boolean;
}

interface CheckboxOption {
  label: string;
  value: string;
}

const { Text } = Typography;

export const Checkbox: React.FC<CheckboxProps> = ({
  id,
  name,
  placeholder,
  options,
  onChange,
  defaultChecked,
  isDisabled,
}) => {
  const { get } = useClient();
  const { user } = useUser();

  const DATA_SOURCE_URL_MAPPER: Associative = {
    categories: '/categories',
    targetDistricts: '/places/districts',
    targetTowns:
      user?.customer.type === 'department'
        ? '/places/towns?only_department=true'
        : '/places/towns',
  };

  const dataSourceUrl =
    name === 'categories'
      ? `${DATA_SOURCE_URL_MAPPER[name]}?type=solidarity`
      : DATA_SOURCE_URL_MAPPER[name];

  const [optionsValues, setOptionsValues] = useState<Array<CheckboxOption>>([]);
  const [optionsCheckedValues, setOptionsCheckedValues] = useState<
    Array<String>
  >([]);

  const filterDataFromApiResponse = (data: any) => {
    switch (name) {
      case 'categories':
        const categories = data.map((dataItem: Associative) => {
          return {
            value: dataItem.id,
            label: dataItem.display_name.replace("\\'", "'"),
          };
        });

        let otherCategorie: Associative = { value: '', label: '' };
        categories.forEach((x: Associative) => {
          if (x.label === 'Autre') otherCategorie = x;
        });

        const sortedCategories = categories
          ?.filter((x: Associative) => x.label !== 'Autre')
          .sort(
            (
              { label: firstLabel }: { value: string; label: string },
              { label: secondLabel }: { value: string; label: string }
            ) => firstLabel.localeCompare(secondLabel)
          );

        return [...sortedCategories, otherCategorie];

      case 'targetTowns':
        if (user?.customer.type === 'department') {
          return Object.entries(data).map(([id, town]: any) => {
            /* For departments we concat the town's name to its ID, to retrieve it later on
             * without having to GET the data again */
            return {
              value: `${town}_${id}`,
              label: town,
            };
          });
        } else {
          /* API response for towns is an Object instead of an array */
          return Object.entries(data).map(([town, { id }]: any) => {
            /* For departments we concat the town's name to its ID, to retrieve it later on
             * without having to GET the data again */
            return {
              value: `${town}_${id}`,
              label: town,
            };
          });
        }
      case 'targetDistricts':
      default:
        return data.map((dataItem: Associative) => {
          return {
            value: dataItem,
            label: dataItem,
          };
        });
    }
  };

  useEffect(() => {
    dataSourceUrl &&
      get<any>(dataSourceUrl, ({ data }) => {
        data && setOptionsValues(filterDataFromApiResponse(data));
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dataSourceUrl, get]);

  useEffect(() => {
    if (defaultChecked && defaultChecked.length > 0) {
      const checkedOptions = Object.values(optionsValues).reduce(
        (checkedValues: Array<string>, option: any) =>
          defaultChecked.includes(option.label)
            ? [...checkedValues, option.value]
            : [...checkedValues],
        []
      );
      setOptionsCheckedValues(checkedOptions);
      checkedOptions && checkedOptions.length > 0 && onChange(checkedOptions);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [optionsValues]);

  const handleChange = (e: any) => {
    let changedOptionsValues = [];
    if (optionsCheckedValues.includes(e.target.value)) {
      const index = optionsCheckedValues.indexOf(e.target.value);
      changedOptionsValues = [
        ...optionsCheckedValues.slice(0, index),
        ...optionsCheckedValues.slice(index + 1),
      ];
    } else {
      changedOptionsValues = [...optionsCheckedValues, e.target.value];
    }
    setOptionsCheckedValues(changedOptionsValues);
    onChange(changedOptionsValues);
  };

  const checkboxOptions = dataSourceUrl ? optionsValues : options;

  return (
    checkboxOptions && (
      <>
        <Text strong={true}>{placeholder}</Text>
        {Object.values(checkboxOptions).map(option => (
          <Row key={option.value}>
            <AntdCheckbox
              id={id}
              value={option.value}
              defaultChecked={
                defaultChecked && defaultChecked.includes(option.label)
              }
              onChange={handleChange}
              disabled={isDisabled}
            >
              {option.label}
            </AntdCheckbox>
          </Row>
        ))}
      </>
    )
  );
};
